// The multiplayer lobby (protocol v2, .ai/multiplayer-protocol-v2.md). The pills say how to play (Quick play, with a
// friend, a code) and lead to the Tables tab. Quick play is a list of the games in the look of the Games window (owner
// 2026-11-01, variant O of branch prototype/mp-quick-list-2026-11-01): no marks, nothing to choose, no big button, no
// descriptions and no games in progress. Each game has its picture, its name, "Create a room" on the right, and its waiting
// rooms in white strips, numbered, with the seats as tokens, the wait and Join. No ad box, the owner's decision for this
// window only. The window (the `multiplayer` window with the class `mp_games`, MultiplayerWindows.tsx) feeds it from the v2
// store; App shows the SearchingBar.
import { Fragment, useEffect, useState, type ReactNode } from 'react';
import { t } from '../i18n';
import { env } from '../env';
import { flagUrl, mpStore, useMultiplayerState, type LobbyState, type RunningGame } from '../multiplayer/store';
import { namesBySeat } from '../online/common';
import { WithFlag } from '../online/PlayerName';
import type { Mp2CatalogGame, Mp2Table } from '../multiplayer/client2';
import { gamePreviewSrc, gamePreviewSrcSet, sharpFlag } from '../options/assets';
import { GAME_IDS, type GameId } from '../engine/types';
import { RULES } from '../engine/rules';
import { useGameOrder } from './GamesWindow';

const mmss = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

function useNow(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  return now;
}

const isCardGame = (g: Mp2CatalogGame | undefined) => g?.kind === 'cards';
/** A game's name online: Kulki's names its version too, as the solitaires' names do ("Kulki - 5 w rzędzie"; owner
 *  2026-11-03: online is always 5 in a row, the easier 4 is for playing alone). */
export const onlineName = (id: string): string => (id === 'kulki' ? `${t('kulki')} - ${t('kulki.line.5')}` : t(id));
/** Kulki (owner 2026-11-03): a race online, but under its own line as in the Games window. */
const isLogicGame = (id: string | undefined) => !!id && (GAME_IDS as readonly string[]).includes(id) && !!RULES[id as GameId].logic;
const COPIED_MS = 3000;

/** A game's minutes: "estimated game 12 min", on phones and low screens (the same query as the list's layout) only "12 min"
 *  (owner 2026-11-01). The lobby renders every second (useNow), so a turned phone follows at once. */
const estTime = (minutes: number): string =>
  matchMedia('(max-width: 839px), (max-height: 614px)').matches ? `${minutes} min` : t('lobby.est-time', { n: minutes });

type Mode = 'quick' | 'friend' | 'code' | 'tables';

/** The lobby opens on the tab the player used last, Quick play or Tables (owner 2026-10-31); a friend's table and a code
 *  are one-off errands and change nothing. Only a convenience: without storage the lobby opens on Quick play. */
const MODE_KEY = 'po-mp-lobby-mode';
function savedMode(): Mode {
  try {
    return localStorage.getItem(MODE_KEY) === 'tables' ? 'tables' : 'quick';
  } catch {
    return 'quick';
  }
}
function saveMode(m: Mode): void {
  try {
    if (m === 'tables') localStorage.setItem(MODE_KEY, m);
    else if (m === 'quick') localStorage.removeItem(MODE_KEY);
  } catch {
    // no storage: the lobby opens on Quick play next time
  }
}

export interface LobbyProps {
  catalog: Mp2CatalogGame[];
  lobby: LobbyState;
  error?: string | null | undefined;
  /** My private table waits for the friend: its code and link. */
  friend?: { code: string; link: string; variant: string } | undefined;
  /** My public race table that waits (Quick play, the window brought back from under the searching bar). */
  mine?: Mp2Table | undefined;
}

export function LobbyView({ catalog, lobby, error, friend, mine }: LobbyProps) {
  const now = useNow();
  const [mode, setMode] = useState<Mode>(savedMode);
  const chooseMode = (m: Mode) => {
    setMode(m);
    saveMode(m);
  };
  // one row per variant, in the Games window's order; a variant it does not list goes last
  const order = useGameOrder();
  const rank = (id: string) => {
    const i = order.indexOf(id as GameId);
    return i < 0 ? order.length : i;
  };
  const rows = catalog.flatMap((g) => g.variants.map((v) => ({ g, v }))).sort((a, b) => rank(a.v.id) - rank(b.v.id));
  const [code, setCode] = useState('');
  // the race: the longest waiting first; a card table: the fullest first (it starts soonest), then the oldest
  const waitingFor = (variant: string) =>
    lobby.tables.filter((tb) => tb.variants.includes(variant)).sort((a, b) => b.players.length - a.players.length || a.since - b.since);
  const lateFor = (variant: string) => lobby.running.filter((r) => r.join && r.variant === variant);
  // a seat at one table at most: sitting elsewhere gives up my own waiting table
  const leaveMine = () => {
    if (mine) mpStore().cancelTable();
  };
  const join = (id: string) => {
    leaveMine();
    mpStore().joinTable(id);
  };
  const pills = (
    <div className="games_sort" role="group" aria-label={t('lobby.how')}>
      <span className="games_sort_label">{t('lobby.how')}</span>
      {(['quick', 'friend', 'code'] as const).map((m) => (
        <button key={m} type="button" className={`games_sort_btn${mode === m && !friend ? ' on' : ''}`} data-mode={m} aria-pressed={mode === m} disabled={!!friend} onClick={() => chooseMode(m)}>
          {t(`lobby.mode.${m}`)}
        </button>
      ))}
      {/* on the right, apart from the "how" pills: not a way to play but what goes on now, every game on one page */}
      <span className="mp_sort_right">
        <span className="mp_counts">
          <span className="mp_dot" aria-hidden="true" />
          {t('lobby.stats', { online: lobby.online, playing: lobby.playing })}
        </span>
        <button type="button" className={`games_sort_btn mp_pill_tables${mode === 'tables' && !friend ? ' on' : ''}`} data-mode="tables" aria-pressed={mode === 'tables'} disabled={!!friend} onClick={() => chooseMode('tables')}>
          {t('lobby.mode.tables')}
          {lobby.tables.length ? <span className="mp_pill_n">{lobby.tables.length}</span> : null}
        </button>
      </span>
    </div>
  );

  let body: ReactNode;
  let buttons: ReactNode;
  if (friend) {
    // my private table waits for the friend: I am seated, so no other game meanwhile
    body = (
      <div className="games_window">
        <div className="mp_friend">
          <div className="mp_friend_title">{t('lobby.friend-title')}: {t(friend.variant)}</div>
          <InviteButton link={friend.link} variant={friend.variant} />
          <div className="mp_friend_alt">
            <span>{t('lobby.invite-code')} <b className="mp_friend_code" aria-label={t('lobby.code-label')}>{friend.code}</b></span>
            <CopyLink link={friend.link} />
          </div>
          <div className="mp_friend_preview">
            <div className="mp_friend_preview_cap">{t('lobby.invite-preview')}</div>
            <div className="mp_friend_preview_box">
              <img src={gamePreviewSrc(friend.variant)} srcSet={gamePreviewSrcSet(friend.variant)} alt="" />
              <div>
                <b>{t('lobby.invite-title', { game: t(friend.variant) })}</b>
                <span>{t('lobby.invite-text')}</span>
                <small>{location.hostname}</small>
              </div>
            </div>
          </div>
          <div className="mp_friend_text">{t('lobby.friend-text')}</div>
        </div>
      </div>
    );
    buttons = <input type="button" className="mp_btn_secondary" value={t('cancel')} onClick={() => mpStore().cancelTable()} />;
  } else if (mode === 'code') {
    body = (
      <div className="games_window">
        <form className="mp_code" onSubmit={(e) => { e.preventDefault(); mpStore().joinCode(code); }}>
          <label htmlFor="mp_code_input">{t('lobby.code-title')}</label>
          <input id="mp_code_input" type="text" autoCapitalize="characters" autoComplete="off" maxLength={4} placeholder={t('lobby.code-placeholder')} value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} />
        </form>
      </div>
    );
    buttons = <input type="button" value={t('lobby.code-join')} onClick={() => mpStore().joinCode(code)} />;
  } else if (mode === 'tables') {
    // the oldest first, so a row stays where it is while players come and go
    const open = [...lobby.tables].sort((a, b) => a.since - b.since);
    const minutes = (variant: string) => catalog.flatMap((g) => g.variants).find((v) => v.id === variant)?.minutes ?? 0;
    body = (
      <div className="games_window">
        <TablesList mine={mine} open={open} running={lobby.running} now={now} minutes={minutes} onJoin={join} />
      </div>
    );
    // no big button: every table has its own (owner 2026-10-31). Only with nothing in the list one leads on to Quick play;
    // it does not change the saved tab, the player still wants to see the tables next time
    if (!open.length && !mine) buttons = <input type="button" value={t('lobby.open-table')} onClick={() => setMode('quick')} />;
  } else {
    const quick = mode === 'quick';
    const minePlayers = mine ? mine.seats.filter((p): p is NonNullable<typeof p> => !!p) : [];
    const mineVariant = mine ? (mine.variant ?? mine.variants[0]) : undefined;
    body = (
      <div className="games_window">
        <div className="mp_list">
          {rows.map(({ g, v }, i) => {
            const isMine = !!mine && mineVariant === v.id;
            const offset = isMine ? 1 : 0;
            return (
              <Fragment key={v.id}>
                {/* the same line as the Games window's (owner 2026-10-30) */}
                {i > 0 && isCardGame(g) && !isCardGame(rows[i - 1]?.g) ? (
                  <div className="games_group" role="separator">
                    <span>{t('games.otherCardGames')}</span>
                  </div>
                ) : null}
                {i > 0 && isLogicGame(v.id) && !isLogicGame(rows[i - 1]?.v.id) ? (
                  <div className="games_group" role="separator">
                    <span>{t('games.otherLogicGames')}</span>
                  </div>
                ) : null}
                <div className="mp_row" data-variant={v.id}>
                  <img className="mp_row_pic" src={gamePreviewSrc(v.id)} srcSet={gamePreviewSrcSet(v.id)} alt="" />
                  <span className="game_name">
                    {onlineName(v.id)} <span className="mp_minutes">· {estTime(v.minutes)}</span>
                  </span>
                  {quick && (isMine || waitingFor(v.id).length || lateFor(v.id).length) ? (
                    <ul className="mp_rooms">
                      {mine && isMine ? (
                        <RoomStrip no={1} id={mine.id} players={minePlayers} seats={mine.seats.length} since={mine.since} now={now} mine>
                          <button type="button" className="mp_rm_btn mp_rm_cancel" onClick={() => mpStore().cancelTable()}>{t('cancel')}</button>
                        </RoomStrip>
                      ) : null}
                      {waitingFor(v.id).map((tb, k) => (
                        <RoomStrip key={tb.id} no={k + 1 + offset} id={tb.id} players={tb.players} seats={tb.seats} since={tb.since} now={now}>
                          <button type="button" className="mp_rm_btn" onClick={() => join(tb.id)}>{t('lobby.room-join')}</button>
                        </RoomStrip>
                      ))}
                      {/* a game in progress with a bot in a seat (owner 2026-11-01, pick C of branch prototype/mp-next-2026-11-01) */}
                      {lateFor(v.id).map((r, k) => {
                        const players = (r.seats ?? []).flatMap((p) => ('name' in p ? [{ name: p.name, flag: p.flag }] : []));
                        return (
                          <RoomStrip key={r.id} no={k + 1 + offset + waitingFor(v.id).length} id={r.id} players={players} seats={r.seats?.length ?? 0} since={r.since} now={now} bots>
                            <button type="button" className="mp_rm_btn mp_rm_late" onClick={() => join(r.id)}>{t('lobby.late-join')}</button>
                          </RoomStrip>
                        );
                      })}
                    </ul>
                  ) : quick ? (
                    // nobody here: a grey stand-in for the first room, so the row does not look empty (owner 2026-11-01)
                    <p className="mp_rm_none">{t('lobby.room-none')}</p>
                  ) : null}
                  {isMine ? null : (
                    <button
                      type="button"
                      className="mp_create"
                      disabled={quick && !!g.rooms && waitingFor(v.id).length >= g.rooms}
                      onClick={() => {
                        leaveMine();
                        if (quick) mpStore().createRoom(g.game, v.id);
                        else mpStore().friend(g.game, v.id);
                      }}
                    >
                      {t(quick ? 'lobby.create-room' : 'lobby.friend-create')}
                    </button>
                  )}
                </div>
              </Fragment>
            );
          })}
        </div>
      </div>
    );
  }
  return (
    <div className="games_wrapper">
      {pills}
      {error ? <div className="mp_error" role="alert">{error}</div> : null}
      {lobby.mineElsewhere && !friend ? <div className="mp_elsewhere">{t('lobby.mine-elsewhere', { game: t(lobby.mineElsewhere) })}</div> : null}
      {body}
      {buttons ? <div className="game_button_cont">{buttons}</div> : null}
    </div>
  );
}

/** A game's name in two parts ("Podwójny Klondike" and "Rozdawanie po jednej karcie"), the kind on its own line; a long
 *  name is never cut. Some languages have no " - " in a name: one part then. */
function TableTitle({ variant, minutes }: { variant: string; minutes: number }) {
  const [family, ...kind] = onlineName(variant).split(' - ');
  return (
    <div className="mp_tb_title">
      <b className="mp_tb_fam">{family}</b>
      {minutes ? <span className="mp_tb_min">{estTime(minutes)}</span> : null}
      {kind.length ? <span className="mp_tb_kind">{kind.join(' - ')}</span> : null}
    </div>
  );
}

/** A player's name with its flag in front (owner 2026-10-31; no country known, a guest: no flag, owner 2026-11-02: the
 *  "?" flag said nothing); a guest's number in grey, so the name reads first. A bot has its own icon instead. */
function TablePlayer({ name, flag, icon }: { name: string; flag?: string | undefined; icon?: ReactNode }) {
  const guest = /^(.+?)(-\d{4})$/.exec(name);
  return (
    <>
      {icon ?? (flag ? <img src={flagUrl(flag)} alt="" /> : null)}
      <span className="mp_tb_name" title={name}>
        {guest ? (
          <>
            {guest[1]}
            <small>{guest[2]}</small>
          </>
        ) : (
          name
        )}
      </span>
    </>
  );
}

/** How long a table waits, the time in bold whatever the language's word order. */
function WaitTime({ since, now }: { since: number; now: number }) {
  const [before = '', after = ''] = t('lobby.waits-for', { time: '@@' }).split('@@');
  return (
    <span className="mp_tb_wait">
      <i className="mp_tb_icon mp_tb_clock" aria-hidden="true" />
      {before}
      <b>{mmss(Math.max(0, Math.round((now - since) / 1000)))}</b>
      {after}
    </span>
  );
}

/** The seats as people: taken and free. */
function TableSeats({ taken, of }: { taken: number; of: number }) {
  return (
    <span className="mp_tb_seats" aria-label={t('lobby.seats', { n: taken, of })}>
      {Array.from({ length: of }, (_, i) => (
        <i key={i} className={`mp_tb_icon mp_tb_seat${i < taken ? ' on' : ''}`} />
      ))}
      <small>{taken}/{of}</small>
    </span>
  );
}

/** A waiting room in Quick play's list: its number, who sits there, the seats as tokens, the wait on a round clock (owner
 *  2026-11-01: "zamiast klepsydry zegarek okragly", the Time box's icon, in the Tables tab too) and the button. A number is only
 *  the place in the list, so it moves up when an earlier room goes. */
function RoomStrip({ no, id, players, seats, since, now, mine, bots, children }: { no: number; id: string; players: { name: string; flag?: string | undefined }[]; seats: number; since: number; now: number; mine?: boolean; bots?: boolean; children: ReactNode }) {
  return (
    <li className={`mp_rm${mine ? ' mp_rm_mine' : ''}`} data-table={id}>
      <span className="mp_rm_info">
        <span className="mp_rm_no">{t('lobby.room-no', { n: no })}</span>
        <span className="mp_rm_who">
          {players.map((p, i) => (
            <span key={i} className="mp_rm_player">
              {p.flag ? <img src={sharpFlag(flagUrl(p.flag))} alt="" width={16} height={11} /> : null}
              <b>{p.name}</b>
              {mine ? <span className="mp_tb_me">{t('lobby.room.you')}</span> : null}
            </span>
          ))}
          {bots ? <span className="mp_rm_bots">{t('lobby.with-bots')}</span> : null}
        </span>
      </span>
      <span className="mp_rm_meta">
        <span className="mp_rm_seats" aria-label={t('lobby.seats', { n: players.length, of: seats })}>
          {Array.from({ length: seats }, (_, i) => (
            <i key={i} className={`mp_tb_icon mp_tb_seat${i < players.length ? ' on' : ''}`} />
          ))}
        </span>
        <span className="mp_rm_time">
          <i className="mp_tb_icon mp_tb_clock" aria-hidden="true" />
          <b>{mmss(Math.max(0, Math.round((now - since) / 1000)))}</b>
        </span>
      </span>
      {children}
    </li>
  );
}

/** The Tables tab (owner 2026-10-31: "na jednej stronie w jednym miejscu bez klikania bylo wiadomo kto gra, kto czeka i ze
 *  mozna do tej grupy graczy dolaczyc"): every open table of every game with its own button, beside every game in
 *  progress. Look f of the prototype round (branch prototype/mp-tables-tab-2026-10-31). Only a table that waits takes a
 *  newcomer (the server's joinable()), so a game in progress has no button. */
function TablesList({ mine, open, running, now, minutes, onJoin }: { mine: Mp2Table | undefined; open: LobbyState['tables']; running: RunningGame[]; now: number; minutes: (variant: string) => number; onJoin: (id: string) => void }) {
  const mineVariant = mine ? (mine.variant ?? mine.variants[0] ?? '') : '';
  const minePlayers = mine ? mine.seats.filter((p): p is NonNullable<typeof p> => !!p) : [];
  return (
    <div className="mp_tb_lists">
      <section className="mp_tb_col" data-list="waiting">
        <h3 className="mp_tb_head">
          {t('lobby.tables.waiting')}
          {open.length ? <span className="mp_tb_n">{open.length}</span> : null}
        </h3>
        {mine || open.length ? (
          <ul>
            {mine ? (
              <li className="mp_tb mp_tb_mine" data-table={mine.id}>
                <img className="mp_tb_pic" src={gamePreviewSrc(mineVariant)} srcSet={gamePreviewSrcSet(mineVariant)} alt="" />
                <TableTitle variant={mineVariant} minutes={minutes(mineVariant)} />
                <div className="mp_tb_slots">
                  {minePlayers.map((p, i) => (
                    <div key={i} className="mp_tb_slot">
                      <TablePlayer name={p.name} flag={p.flag} />
                      <span className="mp_tb_me">{t('lobby.room.you')}</span>
                    </div>
                  ))}
                </div>
                <div className="mp_tb_meta">
                  <WaitTime since={mine.since} now={now} />
                  <TableSeats taken={minePlayers.length} of={mine.seats.length} />
                </div>
                <button type="button" className="mp_tb_btn mp_tb_cancel" onClick={() => mpStore().cancelTable()}>{t('cancel')}</button>
              </li>
            ) : null}
            {open.map((tb) => {
              const variant = tb.variants[0] ?? tb.game;
              return (
                <li key={tb.id} className="mp_tb" data-table={tb.id}>
                  <img className="mp_tb_pic" src={gamePreviewSrc(variant)} srcSet={gamePreviewSrcSet(variant)} alt="" />
                  <TableTitle variant={variant} minutes={minutes(variant)} />
                  <div className="mp_tb_slots">
                    {tb.players.map((p, i) => (
                      <div key={i} className="mp_tb_slot">
                        <TablePlayer name={p.name} flag={p.flag} />
                      </div>
                    ))}
                  </div>
                  <div className="mp_tb_meta">
                    <WaitTime since={tb.since} now={now} />
                    <TableSeats taken={tb.players.length} of={tb.seats} />
                  </div>
                  <button type="button" className="mp_tb_btn" onClick={() => onJoin(tb.id)}>{t('lobby.join')}</button>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="mp_tb_none">{t('lobby.tables.none-waiting')}</p>
        )}
      </section>
      <section className="mp_tb_col" data-list="running">
        <h3 className="mp_tb_head">
          {t('lobby.running')}
          {running.length ? <span className="mp_tb_n">{running.length}</span> : null}
        </h3>
        {running.length ? (
          <ul>
            {running.map((g) => {
              const names = g.seats ? namesBySeat(g.seats, `${g.id}:${g.since}`) : [];
              const values = g.values ?? [];
              // nobody leads while everyone is equal (the start)
              const leads = g.seats && (g.leaders?.length ?? 0) < g.seats.length ? new Set(g.leaders) : new Set<number>();
              return (
                <li key={g.id} className="mp_tb mp_tb_running" data-table={g.id}>
                  <img className="mp_tb_pic" src={gamePreviewSrc(g.variant)} srcSet={gamePreviewSrcSet(g.variant)} alt="" />
                  <TableTitle variant={g.variant} minutes={minutes(g.variant)} />
                  <div className="mp_tb_slots">
                    {g.private || !g.seats ? (
                      <div className="mp_tb_slot mp_tb_private">{t('lobby.running-private')}</div>
                    ) : (
                      g.seats.map((p, i) => (
                        <div key={i} className={`mp_tb_slot${leads.has(i) ? ' mp_tb_lead' : ''}`}>
                          {'name' in p ? (
                            <TablePlayer name={p.name} flag={p.flag} />
                          ) : (
                            <TablePlayer name={t('lobby.seat-bot', { name: names[i] ?? '' })} icon={<i className="mp_tb_icon mp_tb_bot" aria-hidden="true" />} />
                          )}
                          <em aria-label={g.unit === 'cards' ? t('lobby.cards-left', { n: values[i] ?? 0 }) : undefined}>{values[i] ?? 0}</em>
                        </div>
                      ))
                    )}
                  </div>
                  <div className="mp_tb_meta">
                    <span className="mp_tb_wait">
                      <i className="mp_tb_icon mp_tb_clock" aria-hidden="true" />
                      <b>{mmss(Math.max(0, Math.round((now - g.since) / 1000)))}</b>
                    </span>
                  </div>
                  {/* its own row: beside the time it squeezed the game's name (owner 2026-11-01: "tu nachodzi button") */}
                  {g.join ? <button type="button" className="mp_tb_btn mp_tb_late" onClick={() => onJoin(g.id)}>{t('lobby.late-join')}</button> : null}
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="mp_tb_none">{t('lobby.tables.none-playing')}</p>
        )}
      </section>
    </div>
  );
}

export interface RoomProps {
  table: Mp2Table;
  /** My seat at it. */
  me: number;
  /** The table's minutes, from the catalog. */
  minutes: number;
  link?: string | undefined;
}

/**
 * A card table that waits (Makao, Kierki; owner 2026-10-29): who sits there, who wants to start, the free seats a bot
 * takes. The game starts when every seated player pressed "Start with bots" (one alone at a public table, two at a
 * friend's), or at once when the fourth sits down; nobody is ever started against their will, and Leave is always one click.
 */
export function WaitingRoom({ table, me, minutes, link }: RoomProps) {
  const ready = new Set(table.ready ?? []);
  const seated = table.seats.map((p, i) => ({ p, i })).filter((x) => !!x.p);
  const iAmReady = ready.has(me);
  const notReady = seated.filter((x) => !ready.has(x.i) && x.i !== me).map((x) => x.p!.name);
  // one alone may start at a public table (owner 2026-11-01); at a friend's the friend is on the way
  const alone = seated.length === 1 && !table.private;
  const tooFew = seated.length < 2 && table.private;
  const variant = table.variant ?? table.variants[0] ?? table.game;
  let note: string;
  if (alone) note = t('lobby.room.alone');
  else if (tooFew) note = t('lobby.room.need-two');
  else if (iAmReady && notReady.length) note = t('lobby.room.waiting-for', { names: notReady.join(', ') });
  else note = t('lobby.room.how');
  return (
    <div className="games_wrapper">
      <div className="games_window">
        <div className="mp_room">
          <div className="mp_room_head">
            <img className="game_preview" src={gamePreviewSrc(variant)} srcSet={gamePreviewSrcSet(variant)} alt="" />
            <div>
              <div className="game_name">
                {onlineName(variant)} <span className="mp_minutes">· {minutes} min</span>
              </div>
              <div className="mp_room_count">{t('lobby.room.count', { n: seated.length, of: table.seats.length })}</div>
            </div>
          </div>
          {/* as in the friend's window; the copy lives here, three buttons below did not fit a phone (owner 2026-11-02) */}
          {table.private && table.code ? (
            <div className="mp_room_code">
              {link ? <InviteButton link={link} variant={variant} /> : null}
              <div className="mp_friend_alt">
                <span>
                  {t('lobby.invite-code')} <b className="mp_friend_code">{table.code}</b>
                </span>
                {link ? <CopyLink link={link} /> : null}
              </div>
            </div>
          ) : null}
          <ol className="mp_seats">
            {table.seats.map((p, i) => (
              <li key={i} className={`mp_seat${p ? '' : ' mp_seat_free'}${i === me ? ' mp_seat_me' : ''}`}>
                {p ? (
                  <>
                    {p.flag ? <img src={flagUrl(p.flag)} alt="" /> : null}
                    <b>{p.name}</b>
                    {i === me ? <span className="mp_badge">{t('lobby.room.you')}</span> : null}
                    <span className={`mp_seat_state${ready.has(i) ? ' on' : ''}`}>{ready.has(i) ? `✓ ${t('lobby.room.ready')}` : t('lobby.room.not-yet')}</span>
                  </>
                ) : (
                  <>
                    <span className="mp_seat_bot" aria-hidden="true" />
                    <b>{t('lobby.room.free')}</b>
                    <span className="mp_seat_state">{t(alone ? 'lobby.room.bot-until' : 'lobby.room.bot')}</span>
                  </>
                )}
              </li>
            ))}
          </ol>
          <p className={`mp_room_note${tooFew ? ' mp_room_wait' : ''}`}>{note}</p>
        </div>
      </div>
      <div className="game_button_cont">
        <input type="button" className="mp_btn_secondary" value={t('lobby.room.leave')} onClick={() => mpStore().cancelTable()} />
        {iAmReady ? (
          <input type="button" className="mp_btn_secondary mp_unready" value={t('lobby.room.unready')} onClick={() => mpStore().ready(false)} />
        ) : (
          <input type="button" className="mp_start" value={t('lobby.room.start')} disabled={tooFew} onClick={() => mpStore().ready(true)} />
        )}
      </div>
    </div>
  );
}

/** The friend's invitation (owner 2026-11-01, pick A of branch prototype/mp-next-2026-11-01): the phone's own share window;
 *  a browser with none (and any page on plain http) copies the link and says so. */
function InviteButton({ link, variant }: { link: string; variant: string }) {
  const [copied, setCopied] = useState(false);
  const send = () => {
    if (typeof navigator.share === 'function') {
      void navigator.share({ title: t('lobby.invite-title', { game: onlineName(variant) }), text: t('lobby.invite-text'), url: link }).catch(() => undefined);
      return;
    }
    void navigator.clipboard?.writeText(link).then(() => setCopied(true), () => undefined);
  };
  return <button type="button" className="mp_invite" onClick={send}>{t(copied ? 'lobby.invite-copied' : 'lobby.invite')}</button>;
}

/** "Copy the link" says "Link copied" for a moment after a tap, and only when it did copy (owner 2026-11-02). */
function CopyLink({ link }: { link: string }) {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return undefined;
    const id = window.setTimeout(() => setCopied(false), COPIED_MS);
    return () => clearTimeout(id);
  }, [copied]);
  const copy = () => void navigator.clipboard?.writeText(link).then(() => setCopied(true), () => undefined);
  return (
    <button type="button" className="mp_friend_copy" data-link={link} onClick={copy}>
      {t(copied ? 'lobby.invite-copied' : 'lobby.copy-link')}
    </button>
  );
}

/** After Quick play: a small bar over the board while the player goes on with a solo game. */
export function SearchingBar({ game, since, onCancel, other }: { game: string; since: number; onCancel: () => void; other?: { name: string; flag?: string | undefined; variant: string } | undefined }) {
  const now = useNow();
  return (
    <div className={`mp_searching${other ? ' mp_searching_two' : ''}`} role="status">
      <span className="mp_searching_spin" aria-hidden="true" />
      <span className="mp_searching_lines">
        <span className="mp_searching_text">
          <b>{t('lobby.searching', { game: t(game) })}</b>
          <i>{mmss(Math.max(0, Math.round((now - since) / 1000)))}</i>
          {other ? null : <small>{t(env.appTarget === 'multiplayer' ? 'lobby.searching-sub-page' : 'lobby.searching-sub')}</small>}
        </span>
        {/* someone else waits, maybe for another game: nothing automatic, Join leads to the lobby with both rooms */}
        {other ? (
          <span className="mp_searching_other">
            <WithFlag text={t('lobby.also-waits', { name: other.name, game: t(other.variant) })} name={other.name} flag={other.flag} /> ·{' '}
            <button type="button" onClick={() => mpStore().open()}>{t('lobby.room-join')}</button>
          </span>
        ) : null}
      </span>
      <button type="button" onClick={onCancel}>{t('cancel')}</button>
    </div>
  );
}

/** The bar while my public table waits (Quick play), hidden under the countdown. */
export function MpSearching() {
  const mp = useMultiplayerState();
  if (mp.searchClosed && !mp.searching && !mp.countdown && !mp.gameStarted)
    return (
      <div className="mp_searching mp_searching_closed" role="status">
        <span className="mp_searching_text"><b>{t('lobby.idle-closed')}</b></span>
        <button type="button" onClick={() => mpStore().open()}>{t('multiplayer.to-lobby')}</button>
        <button type="button" onClick={() => mpStore().dismissClosed()}>OK</button>
      </div>
    );
  if (!mp.searching || mp.countdown || mp.gameStarted) return null;
  // the longest waiting at another table
  const other = mp.lobby?.tables.filter((tb) => tb.id !== mp.table?.id).sort((a, b) => a.since - b.since)[0];
  return (
    <SearchingBar
      game={mp.searching.game}
      since={mp.searching.since}
      onCancel={() => mpStore().cancelTable()}
      other={other ? { name: other.name, flag: other.flag, variant: other.variants[0] ?? other.game } : undefined}
    />
  );
}
